import { useState, useCallback } from "react";
import servicesService from "../../../api/servicesService";
import eventsService from "../../../api/eventsService";
import { buildIsoFromDateInput, formatDateInput, isInternalCalendarEvent, isGhostOccurrence } from "../utils";
import { DEFAULT_EDIT_FORM } from "../constants";

/**
 * Hook personalizado para manejar la edición de servicios
 */
export const useServiceEditor = (currentDate, onUpdate) => {
  const [selectedService, setSelectedService] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(DEFAULT_EDIT_FORM);
  const [loading, setLoading] = useState(false);

  const openEditor = useCallback((service) => {
    if (!service) {
      setEditForm(DEFAULT_EDIT_FORM);
      return;
    }
    
    setEditForm({
      technicians:
        service.technicians?.length
          ? service.technicians.map((t) => t._id || t)
          : service.technician
          ? [service.technician._id || service.technician]
          : [],
      date:
        (service.isoDate
          ? formatDateInput(service.isoDate)
          : formatDateInput(
              new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                service.date
              )
            )) || "",
      hour: service.hour ?? "",
      type: service.type ?? "",
      title: service.title ?? "",
      eventType: service.eventType ?? "",
      location: service.location ?? "",
      status: service.status ?? "PENDING",
      notes: service.notes ?? "",
    });
    setSelectedService(service);
    setEditing(true);
  }, [currentDate]);

  const closeEditor = useCallback(() => {
    setEditing(false);
    setEditForm(DEFAULT_EDIT_FORM);
  }, []);

  const updateFormField = useCallback((field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveService = useCallback(async () => {
    if (!selectedService) return;
    if (isGhostOccurrence(selectedService)) {
      throw new Error("No se puede editar una previsualización fantasma");
    }

    try {
      setLoading(true);
      
      if (!editForm.date) {
        throw new Error("La fecha es requerida");
      }

      const date = buildIsoFromDateInput(
        editForm.date,
        null,
        currentDate
      );

      if (!date) {
        throw new Error("Fecha inválida");
      }

      const id = selectedService._id || selectedService.id;

      if (isInternalCalendarEvent(selectedService)) {
        await eventsService.update(id, {
          title: editForm.title,
          eventType: editForm.eventType,
          status: editForm.status,
          hour: editForm.hour,
          technicians: editForm.technicians || [],
          technician: editForm.technicians?.[0] || null,
          notes: editForm.notes,
          location: editForm.location || "",
          date,
        });
      } else {
        await servicesService.update(id, {
          type: editForm.type,
          status: editForm.status,
          hour: editForm.hour,
          technicians: editForm.technicians || [],
          notes: editForm.notes,
          date,
        });
      }
      
      closeEditor();
      setSelectedService(null);
      onUpdate?.();
    } catch (err) {
      console.error("Error guardando servicio:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedService, editForm, currentDate, onUpdate, closeEditor]);

  const selectService = useCallback((service) => {
    setSelectedService(service);
    setEditing(false);
  }, []);

  return {
    selectedService,
    editing,
    editForm,
    loading,
    openEditor,
    closeEditor,
    updateFormField,
    saveService,
    selectService,
    setSelectedService,
  };
};

